import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { useThemeMode } from '../../hooks/useThemeMode';
import type { EChartsOption } from 'echarts';
import AppChart from '../charts/AppChart';
import AnimatedMetric from '../shared/AnimatedMetric';
import { CurrencyFormat } from '../shared/Formats';
import classNames from 'classnames';
type ChartMode = 'donut' | 'bar';

const LIGHT_COLORS = ['#2C9464', '#1FBF8F', '#38A3A5', '#F4B740', '#F58D59', '#5C8DFF'];
const DARK_COLORS = ['#6EE7B7', '#2DD4BF', '#60A5FA', '#FCD34D', '#FB7185', '#A78BFA'];
const MODE_OPTIONS: ChartMode[] = ['donut', 'bar'];

export default function CampaignDonationChart() {
  const { filteredStats } = useSelector((state: RootState) => state.dashboard);
  const { isDark } = useThemeMode();

  const [chartMode, setChartMode] = useState<ChartMode>('donut');
  const chartPalette = isDark ? DARK_COLORS : LIGHT_COLORS;
  const labelColor = '#111827';
  const tooltipBackground = isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255,255,255,0.95)';
  const tooltipBorder = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
  const legendText = isDark ? '#E2E8F0' : '#334155';
  const panelSurfaceClass = isDark
    ? 'bg-transparent border-white/10'
    : 'bg-transparent border-white/60';

  const campaigns = useMemo(
    () => filteredStats.campaignBars ?? [],
    [filteredStats.campaignBars],
  );
  const totalDonation = campaigns.reduce((sum, item) => sum + item.totalAmount, 0);
  const topCampaign = campaigns[0];

  const chartData = useMemo(
    () =>
      campaigns.map((item, index) => ({
        name: item.campaignName || 'Unknown',
        value: item.totalAmount,
        itemStyle: {
          color: chartPalette[index % chartPalette.length],
        },
      })),
    [chartPalette, campaigns],
  );

  const donutOption: EChartsOption = {
    backgroundColor: 'transparent',
    color: chartPalette,
    animationDuration: 700,
    animationDurationUpdate: 700,
    tooltip: {
      trigger: 'item',
      backgroundColor: tooltipBackground,
      borderColor: tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: legendText,
        fontSize: 12,
      },
      formatter: (params: any) => {
        const value = CurrencyFormat(params.value || 0);
        return `<strong>${params.name}</strong><br/>${value} (${params.percent}%)`;
      },
    },
    series: [
      {
        name: 'Campaign donations',
        type: 'pie',
        radius: ['46%', '90%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        padAngle: 0,
        universalTransition: true,
        itemStyle: {
          borderRadius: 0,
          borderColor: 'transparent',
          borderWidth: 0,
          shadowBlur: 6,
          shadowColor: isDark ? 'rgba(15, 23, 42, 0.16)' : 'rgba(148, 163, 184, 0.14)',
        },
        label: {
          show: true,
          position: 'inside',
          color: labelColor,
          fontSize: 12,
          fontWeight: 700,
          formatter: '{d}%',
        },
        labelLine: {
          show: false,
        },
        emphasis: {
          scale: true,
          scaleSize: 6,
          itemStyle: {
            shadowBlur: 14,
            shadowColor: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.12)',
          },
        },
        data: chartData,
      },
    ],
  };

  const barOption: EChartsOption = {
    backgroundColor: 'transparent',
    animationDuration: 700,
    animationDurationUpdate: 700,
    grid: {
      left: 8,
      right: 8,
      top: 12,
      bottom: 8,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: tooltipBackground,
      borderColor: tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: legendText,
        fontSize: 12,
      },
      formatter: (params: any) => {
        const item = Array.isArray(params) ? params[0] : params;
        const value = CurrencyFormat(item?.value || 0);
        return `<strong>${item?.name}</strong><br/>${value}`;
      },
    },
    xAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          color: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.18)',
        },
      },
      axisLabel: {
        color: legendText,
      },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: legendText,
        fontSize: 12,
        width: 120,
        overflow: 'truncate',
      },
      data: campaigns.map((item) => item.campaignName || 'Unknown')  as string[],
    },
    series: [
      {
        type: 'bar',
        data: campaigns.map((item, index) => ({
          value: item.totalAmount,
          itemStyle: {
            color: chartPalette[index % chartPalette.length],
            borderRadius: [0, 12, 12, 0],
          },
        })),
        barWidth: 18,
        universalTransition: true,
        label: {
          show: true,
          position: 'right',
          color: labelColor,
          fontWeight: 600,
          formatter: (params: any) => CurrencyFormat(params.value || 0),
        },
      },
    ],
  };

  const chartOption = chartMode === 'donut' ? donutOption : barOption;

  return (
    <div className="h-full min-h-[360px] w-full rounded-[28px] border border-white/60 dark:border-white/10 bg-transparent overflow-hidden">
      <div className="grid h-full min-h-[420px] grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-4 p-4 md:p-5">
        <div className={classNames("relative flex items-center justify-center rounded-[24px] overflow-hidden min-h-[420px] border backdrop-blur-xl", panelSurfaceClass)}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.18),_transparent_45%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.08),_transparent_38%)]" />
          <div className="absolute h-52 w-52 rounded-full bg-white/20 dark:bg-white/5 blur-3xl" />
          <div className="relative z-10 h-[440px] w-full">
            <AppChart option={chartOption} height="100%" />
          </div>
        </div>

        <div className="flex flex-col justify-center gap-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">All campaigns</p>
                <h3 className="mt-1 text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">Donation distribution</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Recorded donation amounts in USD for all campaigns in the selected period.
                </p>
              </div>

              <div className="inline-flex rounded-full border border-white/60 dark:border-white/10 bg-white/45 dark:bg-slate-800/60 p-1 backdrop-blur-sm self-start lg:self-end">
                  {MODE_OPTIONS.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setChartMode(mode)}
                      className={classNames(
                        'rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] transition-colors',
                        chartMode === mode
                          ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-white'
                          : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      )}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/35 dark:bg-white/5 px-4 py-3 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Top campaign</p>
                <p className="mt-2 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{topCampaign?.campaignName || 'No data'}</p>
              </div>
              <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/35 dark:bg-white/5 px-4 py-3 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Total raised</p>
                <AnimatedMetric
                  key={`${chartMode}-summary-${totalDonation}`}
                  value={totalDonation}
                  formatter={CurrencyFormat}
                  className="mt-2 text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {campaigns.map((entry, index) => {
              const percent = totalDonation > 0 ? (entry.totalAmount / totalDonation) * 100 : 0;

              return (
                <div
                  key={`${entry.campaignName}-${index}`}
                  className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/35 dark:bg-white/5 px-4 py-3 backdrop-blur-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                      <span
                        className="mt-1 h-3.5 w-3.5 rounded-full shrink-0 ring-4 ring-white/40 dark:ring-white/5"
                        style={{ backgroundColor: chartPalette[index % chartPalette.length] }}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">
                          {entry.campaignName || 'Unknown'}
                        </p>
                        <p className="mt-0.5 text-xs uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                          Campaign #{index + 1}
                        </p>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {CurrencyFormat(entry.totalAmount)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{percent.toFixed(1)}%</p>
                    </div>
                  </div>

                  <div className="mt-3 h-2 rounded-full bg-slate-200/70 dark:bg-slate-700/70 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(percent, 4)}%`,
                        background: `linear-gradient(90deg, ${chartPalette[index % chartPalette.length]}, ${chartPalette[(index + 1) % chartPalette.length]})`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
